export type SettingsAdoptionFilter = "all" | "changed" | "untouched"

export interface SettingsAdoptionViewRow {
	key: string
	surface: string
	field: string
	label: string
	base: number
	changed: number
}

export interface SettingLocation {
	href: string
	section?: string
	label: string
	order: number
}

export interface SettingsPage {
	href: string
	title: string
}

export interface SettingsLayoutGroup<T> {
	href: string | null
	title: string
	sections: Array<{
		title: string | null
		rows: Array<T & { uiLabel: string }>
	}>
}

export const UNPLACED_GROUP_TITLE = "Not in settings"

export const settingShare = (row: { base: number; changed: number }) =>
	row.base > 0 ? Math.round((row.changed / row.base) * 100) : 0

const matchesFilter = (
	row: SettingsAdoptionViewRow,
	filter: SettingsAdoptionFilter,
) =>
	filter === "all" ||
	(filter === "changed" ? row.changed > 0 : row.changed === 0)

export function groupSettingsByLayout<T extends SettingsAdoptionViewRow>(
	rows: readonly T[],
	locations: Readonly<Record<string, SettingLocation>>,
	pages: readonly SettingsPage[],
	filter: SettingsAdoptionFilter,
	search: string,
): Array<SettingsLayoutGroup<T>> {
	const needle = search.trim().toLowerCase()
	const pageIndex = new Map(pages.map((page, index) => [page.href, index]))
	const pageTitle = new Map(pages.map((page) => [page.href, page.title]))

	const placed = rows.flatMap((row) => {
		if (!matchesFilter(row, filter)) return []
		const location = locations[row.key]
		const uiLabel = location?.label ?? row.label
		const title = location
			? (pageTitle.get(location.href) ?? location.href)
			: UNPLACED_GROUP_TITLE
		if (
			needle &&
			![uiLabel, row.field, title, location?.section ?? "", row.surface].some(
				(text) => text.toLowerCase().includes(needle),
			)
		) {
			return []
		}
		return [{ row: { ...row, uiLabel }, location, title }]
	})

	const rank = (entry: (typeof placed)[number]) =>
		entry.location
			? (pageIndex.get(entry.location.href) ?? pages.length)
			: pages.length + 1

	placed.sort(
		(a, b) =>
			rank(a) - rank(b) ||
			a.title.localeCompare(b.title) ||
			(a.location?.order ?? 0) - (b.location?.order ?? 0) ||
			a.row.surface.localeCompare(b.row.surface) ||
			a.row.uiLabel.localeCompare(b.row.uiLabel),
	)

	const groups: Array<SettingsLayoutGroup<T>> = []
	for (const entry of placed) {
		let group = groups.at(-1)
		if (!group || group.title !== entry.title) {
			group = {
				href: entry.location?.href ?? null,
				title: entry.title,
				sections: [],
			}
			groups.push(group)
		}
		const sectionTitle = entry.location
			? (entry.location.section ?? null)
			: entry.row.surface
		let section = group.sections.at(-1)
		if (!section || section.title !== sectionTitle) {
			section = { title: sectionTitle, rows: [] }
			group.sections.push(section)
		}
		section.rows.push(entry.row)
	}
	return groups
}

// Sidebar order: each top-level page, then its subpages, titled "Parent › Sub".
export function buildSettingsPages(
	items: ReadonlyArray<{ href: string; title: string }>,
	subpages: ReadonlyArray<{ href: string; title: string }>,
	parentBySubpage: Readonly<Record<string, string>>,
): SettingsPage[] {
	return items.flatMap((item) => [
		{ href: item.href, title: item.title },
		...subpages
			.filter((sub) => parentBySubpage[sub.href] === item.href)
			.map((sub) => ({
				href: sub.href,
				title: `${item.title} › ${sub.title}`,
			})),
	])
}
